import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  LinearProgress,
  Tooltip,
  Badge,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CompletedIcon,
  PendingActions as PendingIcon,
  Cancel as CancelledIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CallMade as OutgoingIcon,
  CallReceived as IncomingIcon,
  CallMissed as MissedIcon,
  Assessment as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as TimeIcon,
  Star as StarIcon,
  PlayArrow as PlayIcon,
  School as SchoolIcon,
  Build as ServiceIcon,
  MenuBook as TrainingIcon,
  RateReview as ReviewIcon,
  VerifiedUser as VerificationIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

const TelecallerManagement = () => {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState(0);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [telecallerDialogOpen, setTelecallerDialogOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [telecallers, setTelecallers] = useState([]);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCall, setNewCall] = useState({
    schoolName: "",
    contactPerson: "",
    phone: "",
    callType: "Lead Verification",
    purpose: "",
    scheduledDate: "",
    scheduledTime: "",
    telecaller: "",
    priority: "Medium",
  });
  const [newTelecaller, setNewTelecaller] = useState({
    name: "",
    email: "",
    phone: "",
    daily_target: 20,
    specialization: "",
  });

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    const result = await apiService.getTelecallerDashboard();
    if (result.success && result.data) {
      setTelecallers(Array.isArray(result.data.telecallers) ? result.data.telecallers : []);
      setScheduledCalls(Array.isArray(result.data.scheduledCalls) ? result.data.scheduledCalls : []);
    } else {
      toast.error(result.error || "Failed to load telecaller data");
      setTelecallers([]);
      setScheduledCalls([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const callTypes = [
    { value: "Lead Verification", label: "Lead Verification", icon: <VerificationIcon />, color: crmColors.primary.main },
    { value: "Service Call", label: "Service Call", icon: <ServiceIcon />, color: crmColors.primary.dark },
    { value: "Training Call", label: "Training Call", icon: <TrainingIcon />, color: "#f59e0b" },
    { value: "Review Call", label: "Review Call", icon: <ReviewIcon />, color: "#8b5cf6" },
    { value: "Follow-up Call", label: "Follow-up Call", icon: <PhoneIcon />, color: "#ef4444" },
  ];

  const getCallTypeIcon = (type) => {
    const callType = callTypes.find((ct) => ct.value === type);
    return callType ? callType.icon : <PhoneIcon />;
  };

  const getCallTypeColor = (type) => {
    const callType = callTypes.find((ct) => ct.value === type);
    return callType ? callType.color : theme.palette.primary.main;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "success";
      case "Scheduled":
        return "info";
      case "In Progress":
        return "warning";
      case "Cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <CompletedIcon />;
      case "Scheduled":
        return <ScheduleIcon />;
      case "In Progress":
        return <PendingIcon />;
      case "Cancelled":
        return <CancelledIcon />;
      default:
        return <PhoneIcon />;
    }
  };

  const handleScheduleCall = async () => {
    if (!newCall.schoolName || !newCall.phone || !newCall.scheduledDate || !newCall.scheduledTime) {
      toast.error("Please fill all required fields");
      return;
    }
    const tc = telecallers.find((t) => t.name === newCall.telecaller);
    if (!tc) {
      toast.error("Please select a telecaller");
      return;
    }
    setSaving(true);
    const result = await apiService.createTelecallerCall({
      telecaller_id: tc.telecaller_id || tc.id,
      school_name: newCall.schoolName,
      contact_person: newCall.contactPerson,
      phone: newCall.phone,
      call_type: newCall.callType,
      purpose: newCall.purpose,
      scheduled_date: newCall.scheduledDate,
      scheduled_time: newCall.scheduledTime,
      priority: newCall.priority,
    });
    setSaving(false);
    if (result.success) {
      toast.success("Call scheduled successfully!");
      setScheduleDialogOpen(false);
      setNewCall({
        schoolName: "",
        contactPerson: "",
        phone: "",
        callType: "Lead Verification",
        purpose: "",
        scheduledDate: "",
        scheduledTime: "",
        telecaller: "",
        priority: "Medium",
      });
      fetchDashboard();
    } else {
      toast.error(result.error || "Failed to schedule call");
    }
  };

  const handleAddTelecaller = async () => {
    if (!newTelecaller.name || !newTelecaller.phone) {
      toast.error("Name and phone are required");
      return;
    }
    setSaving(true);
    const result = await apiService.createTelecaller(newTelecaller);
    setSaving(false);
    if (result.success) {
      toast.success("Telecaller added successfully!");
      setTelecallerDialogOpen(false);
      setNewTelecaller({ name: "", email: "", phone: "", daily_target: 20, specialization: "" });
      fetchDashboard();
    } else {
      toast.error(result.error || "Failed to add telecaller");
    }
  };

  const totalCalls = scheduledCalls.length;
  const completedCalls = scheduledCalls.filter((c) => c.status === "Completed").length;
  const scheduledCallsCount = scheduledCalls.filter((c) => c.status === "Scheduled").length;
  const avgSuccessRate = telecallers.length
    ? Math.round(telecallers.reduce((sum, t) => sum + (t.successRate || 0), 0) / telecallers.length)
    : 0;

  if (loading) {
    return (
      <PageContentLayout
        title="Telecaller Management"
        subtitle="Manage telecallers, schedule calls, and track productivity"
        breadcrumbs={[
          { label: "Telecaller Management", icon: <PhoneIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
        ]}
        backTo="/dashboard"
      >
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </PageContentLayout>
    );
  }

  return (
    <PageContentLayout
      title="Telecaller Management"
      subtitle="Manage telecallers, schedule calls, and track productivity"
      breadcrumbs={[
        { label: "Telecaller Management", icon: <PhoneIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
      actions={
        <>
          <Button variant="outlined" size="small" startIcon={<ScheduleIcon />} onClick={() => setScheduleDialogOpen(true)} sx={{ textTransform: "none" }}>
            Schedule Call
          </Button>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setTelecallerDialogOpen(true)} sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
            Add Telecaller
          </Button>
        </>
      }
    >
        {/* Key Metrics */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Total Calls
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {totalCalls}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  {completedCalls} Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Scheduled Calls
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                  {scheduledCallsCount}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  Today: {scheduledCalls.filter((c) => c.scheduledDate === new Date().toISOString().split("T")[0]).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Avg Success Rate
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  {avgSuccessRate}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={avgSuccessRate}
                  sx={{ mt: 1, height: 4, borderRadius: 2 }}
                  color="success"
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Active Telecallers
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                  {telecallers.length}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  {telecallers.reduce((sum, t) => sum + t.todayCalls, 0)} Calls Today
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card sx={{ mb: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Scheduled Calls" />
            <Tab label="Telecallers" />
            <Tab label="Call Analytics" />
          </Tabs>
        </Card>

        {/* Scheduled Calls Tab */}
        {selectedTab === 0 && (
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search calls..."
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                  }}
                  sx={{ flexGrow: 1, minWidth: 250 }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Call Type</InputLabel>
                  <Select label="Call Type" defaultValue="all">
                    <MenuItem value="all">All Types</MenuItem>
                    {callTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" defaultValue="all">
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="Scheduled">Scheduled</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>School</TableCell>
                      <TableCell>Call Type</TableCell>
                      <TableCell>Telecaller</TableCell>
                      <TableCell>Scheduled</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scheduledCalls.map((call) => (
                      <TableRow key={call.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                              {call.schoolName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                              {call.contactPerson} • {call.phone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getCallTypeIcon(call.callType)}
                            label={call.callType}
                            size="small"
                            sx={{
                              fontSize: "0.7rem",
                              height: "22px",
                              bgcolor: alpha(getCallTypeColor(call.callType), 0.1),
                              color: getCallTypeColor(call.callType),
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                            {call.telecaller}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                              {call.scheduledDate}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                              {call.scheduledTime}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={call.priority}
                            size="small"
                            color={call.priority === "High" ? "error" : call.priority === "Medium" ? "warning" : "info"}
                            sx={{ fontSize: "0.7rem", height: "22px" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(call.status)}
                            label={call.status}
                            size="small"
                            color={getStatusColor(call.status)}
                            sx={{ fontSize: "0.7rem", height: "22px" }}
                          />
                        </TableCell>
                        <TableCell>
                          {call.duration ? (
                            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                              {call.duration}
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                            <Tooltip title="Start Call">
                              <IconButton size="small" color="success">
                                <PlayIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Details">
                              <IconButton size="small" color="primary" onClick={() => { setSelectedCall(call); setCallDialogOpen(true); }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Telecallers Tab */}
        {selectedTab === 1 && (
          <Grid container spacing={2}>
            {telecallers.map((telecaller) => (
              <Grid item xs={12} md={4} key={telecaller.id}>
                <Card>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48 }}>
                          {telecaller.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                            {telecaller.name}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {[...Array(5)].map((_, i) => (
                              <StarIcon
                                key={i}
                                fontSize="small"
                                sx={{
                                  color: i < Math.floor(telecaller.rating) ? theme.palette.warning.main : "text.disabled",
                                  fontSize: "0.875rem",
                                }}
                              />
                            ))}
                            <Typography variant="caption" sx={{ ml: 0.5, fontSize: "0.7rem" }}>
                              {telecaller.rating}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Chip
                        label={telecaller.status}
                        size="small"
                        color="success"
                        sx={{ fontSize: "0.7rem", height: "20px" }}
                      />
                    </Box>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: "center", p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                            Total Calls
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.primary.main }}>
                            {telecaller.totalCalls}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: "center", p: 1, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                            Success Rate
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.success.main }}>
                            {telecaller.successRate}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                          Today's Progress
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                          {telecaller.todayCalls}/{telecaller.todayTarget}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={((telecaller.todayCalls / Math.max(telecaller.todayTarget || 1, 1)) * 100)}
                        sx={{ height: 6, borderRadius: 3 }}
                        color={telecaller.todayCalls >= telecaller.todayTarget ? "success" : "primary"}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary", display: "block", mb: 0.5 }}>
                        Specialization
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {telecaller.specialization.map((spec, idx) => (
                          <Chip
                            key={idx}
                            label={spec}
                            size="small"
                            sx={{ fontSize: "0.65rem", height: "20px" }}
                          />
                        ))}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button variant="outlined" size="small" fullWidth startIcon={<AnalyticsIcon />}>
                        Analytics
                      </Button>
                      <Button variant="outlined" size="small" fullWidth startIcon={<EditIcon />}>
                        Edit
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Call Analytics Tab */}
        {selectedTab === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: "1rem" }}>
                    Call Type Distribution
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {callTypes.map((type) => {
                      const count = scheduledCalls.filter((c) => c.callType === type.value).length;
                      const percentage = (count / totalCalls) * 100;
                      return (
                        <Box key={type.value}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box sx={{ color: type.color }}>{type.icon}</Box>
                              <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                                {type.label}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                              {count} ({percentage.toFixed(0)}%)
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{ height: 6, borderRadius: 3 }}
                            style={{ backgroundColor: alpha(type.color, 0.1) }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: "1rem" }}>
                    Performance Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: "center", p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block", mb: 0.5 }}>
                          Avg Call Duration
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.primary.main }}>
                          13:45
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: "center", p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block", mb: 0.5 }}>
                          Completion Rate
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.success.main }}>
                          {Math.round((completedCalls / totalCalls) * 100)}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: "center", p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block", mb: 0.5 }}>
                          Calls Today
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.info.main }}>
                          {telecallers.reduce((sum, t) => sum + t.todayCalls, 0)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: "center", p: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block", mb: 0.5 }}>
                          Pending Calls
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.warning.main }}>
                          {scheduledCallsCount}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Schedule Call Dialog */}
        <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Schedule Call</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="School Name"
                  value={newCall.schoolName}
                  onChange={(e) => setNewCall({ ...newCall, schoolName: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={newCall.contactPerson}
                  onChange={(e) => setNewCall({ ...newCall, contactPerson: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={newCall.phone}
                  onChange={(e) => setNewCall({ ...newCall, phone: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Call Type</InputLabel>
                  <Select
                    value={newCall.callType}
                    label="Call Type"
                    onChange={(e) => setNewCall({ ...newCall, callType: e.target.value })}
                  >
                    {callTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Purpose/Notes"
                  multiline
                  rows={2}
                  value={newCall.purpose}
                  onChange={(e) => setNewCall({ ...newCall, purpose: e.target.value })}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Scheduled Date"
                  type="date"
                  value={newCall.scheduledDate}
                  onChange={(e) => setNewCall({ ...newCall, scheduledDate: e.target.value })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Scheduled Time"
                  type="time"
                  value={newCall.scheduledTime}
                  onChange={(e) => setNewCall({ ...newCall, scheduledTime: e.target.value })}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Assign Telecaller</InputLabel>
                  <Select
                    value={newCall.telecaller}
                    label="Assign Telecaller"
                    onChange={(e) => setNewCall({ ...newCall, telecaller: e.target.value })}
                  >
                    {telecallers.map((t) => (
                      <MenuItem key={t.id} value={t.name}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={newCall.priority}
                    label="Priority"
                    onChange={(e) => setNewCall({ ...newCall, priority: e.target.value })}
                  >
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="Low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScheduleDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={handleScheduleCall} disabled={saving}>
              {saving ? "Scheduling…" : "Schedule Call"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Call Details Dialog */}
        <Dialog open={callDialogOpen} onClose={() => setCallDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Call Details</DialogTitle>
          <DialogContent>
            {selectedCall && (
              <Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      School
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedCall.schoolName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      Contact Person
                    </Typography>
                    <Typography variant="body1">{selectedCall.contactPerson}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      Phone
                    </Typography>
                    <Typography variant="body1">{selectedCall.phone}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      Call Type
                    </Typography>
                    <Chip
                      icon={getCallTypeIcon(selectedCall.callType)}
                      label={selectedCall.callType}
                      size="small"
                      sx={{
                        bgcolor: alpha(getCallTypeColor(selectedCall.callType), 0.1),
                        color: getCallTypeColor(selectedCall.callType),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      Purpose
                    </Typography>
                    <Typography variant="body2">{selectedCall.purpose}</Typography>
                  </Grid>
                  {selectedCall.outcome && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                        Outcome
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.success.main, fontWeight: 600 }}>
                        {selectedCall.outcome}
                      </Typography>
                    </Grid>
                  )}
                  {selectedCall.notes && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                        Notes
                      </Typography>
                      <Typography variant="body2">{selectedCall.notes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCallDialogOpen(false)}>Close</Button>
            {selectedCall?.status === "Scheduled" && (
              <Button variant="contained" startIcon={<PlayIcon />}>
                Start Call
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog open={telecallerDialogOpen} onClose={() => setTelecallerDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Telecaller</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  size="small"
                  required
                  value={newTelecaller.name}
                  onChange={(e) => setNewTelecaller({ ...newTelecaller, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  size="small"
                  required
                  value={newTelecaller.phone}
                  onChange={(e) => setNewTelecaller({ ...newTelecaller, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  size="small"
                  value={newTelecaller.email}
                  onChange={(e) => setNewTelecaller({ ...newTelecaller, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Daily Target"
                  type="number"
                  size="small"
                  value={newTelecaller.daily_target}
                  onChange={(e) => setNewTelecaller({ ...newTelecaller, daily_target: Number(e.target.value) || 20 })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Specialization"
                  size="small"
                  placeholder="Lead Verification, Service Calls"
                  value={newTelecaller.specialization}
                  onChange={(e) => setNewTelecaller({ ...newTelecaller, specialization: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTelecallerDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={handleAddTelecaller} disabled={saving}>
              {saving ? "Adding…" : "Add Telecaller"}
            </Button>
          </DialogActions>
        </Dialog>
    </PageContentLayout>
  );
};

export default TelecallerManagement;

